// The Engineers card on a subcontractor's page (Session BAV): link an existing engineer to this firm, unlink one, or add a
// new engineer login already linked to it.
//
// NOTHING HERE RE-IMPLEMENTS THE LINK. Link and unlink go through the SAME route the Engineers page uses
// (PATCH /api/engineers/:id with subcontractorId), so its approved-weeks preview and its 409 approved_weeks_affected
// confirm (subcontractorRates/linkChange.ts) are inherited. Every change is previewed first and confirmed in a branded
// panel that names the engineer and the firm, and, when a link change would move an already approved week's pay check
// figure, names each week with a long date and needs a tick. Add engineer is the ordinary create with a subcontractorId.
// Whether the firm "supplies engineers" is derived live by the server from its active linked engineers (never a flag):
// it is what decides whether the firm's portal shows the Engineer app card.
//
// Session BCE: an engineer has no password and no username. They sign in with their email and an emailed 6-digit code, so
// Add engineer asks for the email and mobile (both required) and "Send app details" leaves a Gmail draft with the app
// details. A linked engineer missing either gets an amber note. Email and mobile are read from the engineers list this
// card already loads, matched by user id.
import { useCallback, useEffect, useState } from "react";
import { ApiError, api } from "../../lib/api";
import type { EngineerListItem, RateCardApprovedWeek, SubcontractorEngineersView } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { Loader } from "../../components/Loader";
import { formatDateLong } from "../../lib/format";
import { SendAppDetailsButton } from "../../components/SendAppDetailsButton";
import {
  ENGINEER_NEEDS_DETAILS_NOTE,
  EMAIL_TAKEN_MESSAGE,
  sharesEmailNote,
  engineerEmailProblem,
  engineerMobileProblem,
  engineerNeedsDetails,
} from "../../lib/engineerContact";

const btnCls = "rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong disabled:opacity-50";
const inputCls = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green";

interface Pending {
  userId: string;
  name: string;
  /** The firm the engineer is moving to, or null for an unlink. */
  to: string | null;
  fromCompany: string | null;
  weeks: RateCardApprovedWeek[];
}

export function SubcontractorEngineersSection({ subId, company, archived }: { subId: string; company: string; archived: boolean }) {
  const [view, setView] = useState<SubcontractorEngineersView | null>(null);
  const [all, setAll] = useState<EngineerListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pick, setPick] = useState("");
  const [pending, setPending] = useState<Pending | null>(null);
  const [ticked, setTicked] = useState(false);
  const [adding, setAdding] = useState(false);
  const [formErrors, setFormErrors] = useState<{ email?: string; mobile?: string }>({});
  const [form, setForm] = useState({ displayName: "", email: "", mobile: "" });

  const load = useCallback(() => {
    api.subcontractors.engineers(subId).then(setView, (err) => setError(friendlyApiError(err, "Could not load the engineers.")));
    api.engineers.list().then(setAll, () => setAll([]));
  }, [subId]);

  useEffect(() => {
    load();
  }, [load]);

  const linkable = (all ?? []).filter((e) => e.subcontractorId !== subId);

  // Step one: ask the route what the change would do. Nothing is written.
  async function propose(engineer: { userId: string; name: string; fromCompany: string | null }, to: string | null) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const preview = await api.engineers.previewSubcontractorLink(engineer.userId, to);
      setTicked(false);
      setPending({ ...engineer, to, weeks: preview.approvedWeeksMoved });
    } catch (err) {
      setError(friendlyApiError(err, "Could not check what that change would do. Nothing was changed."));
    } finally {
      setBusy(false);
    }
  }

  // Step two: the office's confirmation, sent with the approved-weeks acknowledgement only when weeks were named.
  async function confirm() {
    if (!pending) return;
    setBusy(true);
    setError(null);
    try {
      await api.engineers.update(pending.userId, {
        subcontractorId: pending.to,
        ...(pending.weeks.length > 0 && ticked ? { affectsApprovedWeeks: true as const } : {}),
      });
      setNotice(pending.to ? `${pending.name} is now linked to ${company}.` : `${pending.name} is no longer linked to ${company}.`);
      setPending(null);
      setPick("");
      load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && err.body?.error === "approved_weeks_affected") {
        // A week was approved between the preview and the confirm: show the server's sentence and ask again.
        setError(err.message);
        setPending(null);
      } else {
        setError(friendlyApiError(err, "That did not work. Nothing was changed."));
      }
    } finally {
      setBusy(false);
    }
  }

  async function addEngineer() {
    const emailProblem = engineerEmailProblem(form.email);
    const mobileProblem = engineerMobileProblem(form.mobile);
    if (emailProblem || mobileProblem) {
      setFormErrors({ ...(emailProblem ? { email: emailProblem } : {}), ...(mobileProblem ? { mobile: mobileProblem } : {}) });
      return;
    }
    setFormErrors({});
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const created = await api.engineers.create({
        displayName: form.displayName.trim(),
        email: form.email.trim(),
        mobile: form.mobile.trim(),
        subcontractorId: subId,
      });
      const draft = created.appDetails;
      setNotice(
        `${created.displayName} was added and linked to ${company}. They sign in with their email and a code we email to it.${
          draft?.drafted ? ` A draft with the app details is waiting in Gmail for ${draft.to}. Open Gmail and press Send.` : " Use Send app details to leave them the app details."
        }`,
      );
      setForm({ displayName: "", email: "", mobile: "" });
      setAdding(false);
      load();
    } catch (err) {
      if (err instanceof ApiError && err.code === "email_taken") setFormErrors({ email: EMAIL_TAKEN_MESSAGE });
      else if (err instanceof ApiError && err.details && (err.details.email || err.details.mobile)) {
        setFormErrors({ email: err.details.email?.[0], mobile: err.details.mobile?.[0] });
      } else setError(friendlyApiError(err, "Could not add the engineer. Please check the details and try again."));
    } finally {
      setBusy(false);
    }
  }

  const headline = (p: Pending): string => {
    const n = p.weeks.length;
    const count = `${n} already approved week${n === 1 ? "" : "s"}`;
    if (p.to === null) return `Unlinking ${p.name} from ${company} changes the pay check figure of ${count} from priced to unpriced:`;
    if (p.fromCompany) return `Moving ${p.name} from ${p.fromCompany} to ${company} changes the pay check figure of ${count} from one firm's rate card to the other's:`;
    return `Linking ${p.name} to ${company} changes the pay check figure of ${count} from unpriced to priced at this firm's rate card:`;
  };

  return (
    <section className="mb-5 rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
      <h2 className="mb-3 font-heading text-lg uppercase tracking-wide text-ele-blue">Engineers</h2>
      <p className="mb-3 max-w-2xl text-sm text-ele-body">
        The engineers who work for ELE through this firm. A linked engineer is paid from this firm's rate card, and the firm's portal shows the work
        booked for them. While at least one linked engineer is active, the firm's portal also shows an Engineer app card.
      </p>
      {error && <p role="alert" className="mb-3 rounded-ele border border-ele-danger px-3 py-2 text-sm text-ele-strong">{error}</p>}
      {notice && <p className="mb-3 rounded-ele border border-ele-green px-3 py-2 text-sm text-ele-strong">{notice}</p>}

      {view === null ? (
        <Loader label="Loading the engineers..." />
      ) : (
        <>
          <p className="mb-2 text-sm text-ele-body">
            {view.suppliesEngineers ? "This firm supplies engineers: its portal shows the Engineer app card." : "No active engineer is linked, so this firm's portal shows no Engineer app card."}
          </p>
          {view.engineers.length === 0 ? (
            <p className="mb-3 text-sm text-ele-body">No engineer is linked to this firm yet.</p>
          ) : (
            <ul className="mb-4 space-y-2">
              {view.engineers.map((e) => {
                const row = e.userId ? (all ?? []).find((x) => x.id === e.userId) : undefined;
                const needsDetails = row ? engineerNeedsDetails(row) : false;
                return (
                <li key={e.id} className="flex flex-wrap items-center justify-between gap-2 rounded-ele border border-ele-purple-accent px-3 py-2">
                  <div>
                    <p className="text-sm text-ele-strong">{e.name}</p>
                    <p className="text-xs text-ele-body">
                      {e.active ? "Active" : "Deactivated"}
                      {row?.email ? ` · ${row.email}` : ""}
                    </p>
                    {needsDetails ? <p className="mt-1 text-xs text-[var(--ele-amber-text-soft)]">{ENGINEER_NEEDS_DETAILS_NOTE}</p> : null}
                    {sharesEmailNote(row?.sharesEmailWith) ? <p className="mt-1 text-xs text-ele-body">{sharesEmailNote(row?.sharesEmailWith)}</p> : null}
                  </div>
                  {e.userId ? (
                    <span className="flex flex-wrap items-center gap-2">
                      {/* Session BCE: the route preview names the address, or says why there is none. */}
                      {e.active ? <SendAppDetailsButton userId={e.userId} name={e.name} /> : null}
                      <button type="button" className={btnCls} disabled={busy} onClick={() => propose({ userId: e.userId!, name: e.name, fromCompany: company }, null)}>
                        Unlink
                      </button>
                    </span>
                  ) : null}
                </li>
                );
              })}
            </ul>
          )}

          {pending && (
            <div role="status" className="mb-4 rounded-ele border border-ele-purple-accent px-3 py-2 text-sm">
              {pending.weeks.length > 0 ? (
                <>
                  <p className="text-ele-strong">{headline(pending)}</p>
                  <ul className="mt-1 list-disc pl-5 text-ele-body">
                    {pending.weeks.map((w) => (
                      <li key={`${w.engineerId}-${w.weekStarting}`}>week beginning {formatDateLong(w.weekStarting)}</li>
                    ))}
                  </ul>
                  <p className="mt-1 text-xs text-ele-body opacity-70">An approved week's check figure is derived live, so it would move. Nothing has been saved yet.</p>
                  <label className="mt-2 flex items-center gap-2 text-ele-body">
                    <input type="checkbox" checked={ticked} onChange={(e) => setTicked(e.target.checked)} />
                    Yes, change the check figure of {pending.weeks.length === 1 ? "that approved week" : "those approved weeks"}. The change is recorded against my name.
                  </label>
                </>
              ) : (
                <p className="text-ele-strong">
                  {pending.to === null ? `Unlink ${pending.name} from ${company}?` : pending.fromCompany ? `Move ${pending.name} from ${pending.fromCompany} to ${company}?` : `Link ${pending.name} to ${company}?`} No
                  already approved week would change its check figure.
                </p>
              )}
              <div className="mt-2 flex gap-2">
                <button type="button" className={btnCls} disabled={busy || (pending.weeks.length > 0 && !ticked)} onClick={() => void confirm()}>
                  {pending.to === null ? "Confirm unlink" : "Confirm link"}
                </button>
                <button type="button" className={btnCls} disabled={busy} onClick={() => setPending(null)}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {archived ? (
            <p className="text-sm text-ele-body">This subcontractor is archived. Restore it to link or add engineers.</p>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap items-end gap-2">
                <label className="text-xs text-ele-body max-sm:w-full">
                  Link an engineer
                  {/* sm:min-w-64, not min-w-64 (Session BDY): a 256px minimum is wider than the card on a 360px phone at a larger text size. */}
                  <select className={`${inputCls} mt-1 sm:min-w-64`} value={pick} onChange={(e) => setPick(e.target.value)} disabled={all === null}>
                    <option value="">Choose an engineer...</option>
                    {linkable.map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.displayName}
                        {e.subcontractorCompany ? ` (linked to ${e.subcontractorCompany})` : ""}
                        {e.isActive ? "" : " (deactivated)"}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  className={btnCls}
                  disabled={busy || !pick}
                  onClick={() => {
                    const e = linkable.find((x) => x.id === pick);
                    if (e) void propose({ userId: e.id, name: e.displayName, fromCompany: e.subcontractorCompany }, subId);
                  }}
                >
                  Link
                </button>
              </div>

              {adding ? (
                <form
                  className="grid max-w-xl gap-2 sm:grid-cols-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void addEngineer();
                  }}
                >
                  <label className="text-xs text-ele-body">
                    Name
                    <input className={`${inputCls} mt-1`} value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} />
                  </label>
                  <label className="text-xs text-ele-body">
                    Email (required)
                    <input className={`${inputCls} mt-1`} type="email" autoCapitalize="none" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                    {formErrors.email && <span role="alert" className="mt-1 block text-xs text-ele-danger">{formErrors.email}</span>}
                  </label>
                  <label className="text-xs text-ele-body">
                    Mobile (required)
                    <input className={`${inputCls} mt-1`} value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} />
                    {formErrors.mobile && <span role="alert" className="mt-1 block text-xs text-ele-danger">{formErrors.mobile}</span>}
                  </label>
                  <div className="flex items-end gap-2">
                    <button type="submit" className={btnCls} disabled={busy || !form.displayName.trim() || !form.email.trim() || !form.mobile.trim()}>
                      Add engineer
                    </button>
                    <button type="button" className={btnCls} disabled={busy} onClick={() => setAdding(false)}>
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <button type="button" className={btnCls} onClick={() => setAdding(true)}>
                  Add engineer
                </button>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}
